import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, Loader2, Save, Copy as CopyIcon, Check, ArrowLeft, ChevronDown, AlertTriangle, X as XIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { PageMeta } from "@/components/PageMeta";
import { TeamFlag } from "@/components/TeamFlag";
import { MatchData } from "@/components/MatchCard";
import { formatKickoff, isLocked, buildBulkCopyRows } from "@/lib/utils-app";
import { toast } from "sonner";

interface League {
  id: string;
  name: string;
  tournament_id: string | null;
  lock_minutes_before_kickoff: number;
}
interface Prediction {
  id: string;
  league_id: string;
  match_id: string;
  user_id: string;
  predicted_home_score: number;
  predicted_away_score: number;
}

const BulkTips = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [matches, setMatches] = useState<MatchData[]>([]);
  const [predictions, setPredictions] = useState<Prediction[]>([]);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const { data: lm } = await supabase
      .from("league_members")
      .select("league_id, leagues:league_id(id,name,tournament_id,lock_minutes_before_kickoff)")
      .eq("user_id", user.id);
    const lgs: League[] = ((lm as any[]) ?? [])
      .map((r) => r.leagues)
      .filter(Boolean);
    setLeagues(lgs);
    const tids = Array.from(new Set(lgs.map((l) => l.tournament_id).filter(Boolean))) as string[];
    if (tids.length) {
      const { data: m } = await supabase
        .from("matches")
        .select("*")
        .in("tournament_id", tids)
        .order("kickoff_at");
      setMatches((m as MatchData[]) ?? []);
    } else {
      setMatches([]);
    }
    const lids = lgs.map((l) => l.id);
    if (lids.length) {
      const { data: pr } = await supabase
        .from("predictions")
        .select("*")
        .eq("user_id", user.id)
        .in("league_id", lids);
      setPredictions((pr as Prediction[]) ?? []);
    } else {
      setPredictions([]);
    }
    setLoading(false);
  };

  const mergePredictions = (incoming: Prediction[]) => {
    setPredictions((prev) => {
      const map = new Map(prev.map((p) => [`${p.league_id}:${p.match_id}`, p]));
      incoming.forEach((p) => map.set(`${p.league_id}:${p.match_id}`, p));
      return Array.from(map.values());
    });
  };

  useEffect(() => { load(); }, [user]);

  // Future matches (kickoff in future) — bulk tipping only makes sense for upcoming
  const upcoming = useMemo(
    () => matches.filter((m) => new Date(m.kickoff_at).getTime() > Date.now()),
    [matches]
  );

  // predictions index: leagueId -> matchId -> prediction
  const predIndex = useMemo(() => {
    const idx: Record<string, Record<string, Prediction>> = {};
    predictions.forEach((p) => {
      (idx[p.league_id] = idx[p.league_id] || {})[p.match_id] = p;
    });
    return idx;
  }, [predictions]);

  return (
    <AppShell title={t("bulk.title")}>
      <PageMeta
        title="Hromadné tipy — Tipomatic"
        description="Zadej tipy na všechny zápasy najednou napříč svými tipovacími ligami."
        path="/predict"
      />
      <Link to="/" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-4 w-4 mr-1" />{t("common.back")}
      </Link>

      <div className="mb-5">
        <h1 className="text-2xl font-bold">{t("bulk.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t("bulk.subtitle")}</p>
      </div>

      {loading ? (
        <p className="text-muted-foreground">{t("common.loading")}</p>
      ) : leagues.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground">{t("bulk.noLeagues")}</CardContent></Card>
      ) : (
        <div className="space-y-6">
          {leagues.length >= 2 && (
            <CopySection leagues={leagues} matches={matches} predictions={predictions} userId={user!.id} onDone={mergePredictions} />
          )}

          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              {t("bulk.upcomingMatches")} ({upcoming.length})
            </h2>
            {upcoming.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-muted-foreground">{t("bulk.noUpcoming")}</CardContent></Card>
            ) : (
              <div className="space-y-2">
                {upcoming.map((m) => (
                  <BulkMatchRow
                    key={m.id}
                    match={m}
                    leagues={leagues}
                    predIndex={predIndex}
                    userId={user!.id}
                    onSaved={mergePredictions}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </AppShell>
  );
};

/* -------------------- bulk match row -------------------- */

const BulkMatchRow = ({
  match, leagues, predIndex, userId, onSaved,
}: {
  match: MatchData;
  leagues: League[];
  predIndex: Record<string, Record<string, Prediction>>;
  userId: string;
  onSaved: (rows: Prediction[]) => void;
}) => {
  const { t } = useTranslation();

  // eligible leagues = same tournament + not locked
  const eligible = useMemo(
    () => leagues.filter(
      (l) => l.tournament_id === match.tournament_id && !isLocked(match.kickoff_at, l.lock_minutes_before_kickoff)
    ),
    [leagues, match]
  );

  // initial score: prefer most-recent existing prediction across eligible leagues
  const existingForMatch = useMemo(() => {
    for (const l of eligible) {
      const p = predIndex[l.id]?.[match.id];
      if (p) return p;
    }
    return undefined;
  }, [eligible, predIndex, match.id]);

  const [h, setH] = useState<number>(existingForMatch?.predicted_home_score ?? 0);
  const [a, setA] = useState<number>(existingForMatch?.predicted_away_score ?? 0);
  const [selected, setSelected] = useState<Record<string, boolean>>(
    () => Object.fromEntries(eligible.map((l) => [l.id, true]))
  );
  const [busy, setBusy] = useState(false);
  type SaveStatus = "saved" | "unique" | "error";
  const [lastStatus, setLastStatus] = useState<Record<string, SaveStatus>>({});

  useEffect(() => {
    setSelected((prev) => {
      const next: Record<string, boolean> = {};
      eligible.forEach((l) => { next[l.id] = prev[l.id] ?? true; });
      return next;
    });
  }, [eligible]);

  // Clear per-league status when user edits the score (stale signal)
  useEffect(() => { setLastStatus({}); }, [h, a]);

  const Stepper = ({ value, set }: { value: number; set: (n: number) => void }) => (
    <div className="flex items-center gap-1">
      <Button type="button" variant="outline" size="icon" className="h-9 w-9 rounded-full" disabled={busy || value <= 0} onClick={() => set(Math.max(0, value - 1))}>−</Button>
      <Input type="number" inputMode="numeric" min={0} max={20} value={value}
        onChange={(e) => set(Math.max(0, Math.min(20, parseInt(e.target.value) || 0)))}
        disabled={busy} className="w-12 h-9 px-1 text-center font-bold tabular-nums" />
      <Button type="button" variant="outline" size="icon" className="h-9 w-9 rounded-full" disabled={busy || value >= 20} onClick={() => set(Math.min(20, value + 1))}>+</Button>
    </div>
  );

  const targetLeagueIds = eligible.filter((l) => selected[l.id]).map((l) => l.id);

  const save = async () => {
    if (targetLeagueIds.length === 0) {
      toast.error(t("bulk.pickAtLeastOne"));
      return;
    }
    setBusy(true);
    const succeeded: string[] = [];
    const uniqueSkipped: string[] = []; // league names
    const otherErrors: string[] = [];
    const status: Record<string, SaveStatus> = {};
    for (const lid of targetLeagueIds) {
      const row = {
        league_id: lid,
        match_id: match.id,
        user_id: userId,
        predicted_home_score: h,
        predicted_away_score: a,
      };
      const { error } = await supabase
        .from("predictions")
        .upsert(row, { onConflict: "league_id,match_id,user_id" });
      if (!error) {
        succeeded.push(lid);
        status[lid] = "saved";
      } else if (
        error.hint === "unique_results_taken" ||
        error.message?.includes("already taken by another player")
      ) {
        uniqueSkipped.push(leagues.find((l) => l.id === lid)?.name ?? lid);
        status[lid] = "unique";
      } else {
        otherErrors.push(error.message);
        status[lid] = "error";
      }
    }
    setLastStatus(status);
    setBusy(false);
    const dedupedErrors = [...new Set(otherErrors)];
    if (succeeded.length) {
      const descParts: string[] = [];
      if (uniqueSkipped.length) {
        descParts.push(t("bulk.uniqueSkipped", { home: h, away: a, leagues: uniqueSkipped.join(", ") }));
      }
      if (dedupedErrors.length) {
        descParts.push(...dedupedErrors);
      }
      toast.success(t("bulk.savedToN", { count: succeeded.length }), {
        description: descParts.length ? descParts.join("\n") : undefined,
      });
      const savedRows: Prediction[] = succeeded.map((lid) => {
        const existing = predIndex[lid]?.[match.id];
        return {
          id: existing?.id ?? crypto.randomUUID(),
          league_id: lid,
          match_id: match.id,
          user_id: userId,
          predicted_home_score: h,
          predicted_away_score: a,
        };
      });
      onSaved(savedRows);
    } else if (uniqueSkipped.length || dedupedErrors.length) {
      const parts: string[] = [];
      if (uniqueSkipped.length) {
        parts.push(t("bulk.uniqueSkipped", { home: h, away: a, leagues: uniqueSkipped.join(", ") }));
      }
      if (dedupedErrors.length) {
        parts.push(...dedupedErrors);
      }
      toast.error(parts.join("\n"));
    }
  };


  const locked = eligible.length === 0;

  return (
    <Card className="rounded-2xl">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span>{formatKickoff(match.kickoff_at)}</span>
          {match.group_name && <Badge variant="secondary" className="text-[10px] rounded-full">{t("phases.groupShort")} {match.group_name}</Badge>}
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2 sm:flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <TeamFlag team={match.home_team} size="md" className="h-5 w-7 sm:h-6 sm:w-9" />
              <span className="font-semibold text-sm truncate">{match.home_team}</span>
            </div>
            <div className="flex items-center gap-2 min-w-0 justify-end">
              <span className="font-semibold text-sm truncate text-right">{match.away_team}</span>
              <TeamFlag team={match.away_team} size="md" className="h-5 w-7 sm:h-6 sm:w-9" />
            </div>
          </div>
          <div className="flex items-center justify-center gap-2">
            <Stepper value={h} set={setH} />
            <span className="text-muted-foreground font-bold">:</span>
            <Stepper value={a} set={setA} />
          </div>
        </div>

        {locked ? (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground bg-muted/40 rounded-md px-3 py-2">
            <Lock className="h-3 w-3" />
            <span>{t("bulk.noEligibleLeagues")}</span>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5">
              {leagues
                .filter((l) => l.tournament_id === match.tournament_id)
                .map((l) => {
                  const isEligible = eligible.some((e) => e.id === l.id);
                  const existing = predIndex[l.id]?.[match.id];
                  const checked = !!selected[l.id] && isEligible;
                  const status = lastStatus[l.id];
                  const reasonLabel = !isEligible
                    ? t("bulk.status.skippedLocked")
                    : status === "saved"
                      ? t("bulk.status.saved")
                      : status === "unique"
                        ? t("bulk.status.skippedUnique")
                        : status === "error"
                          ? t("bulk.status.error")
                          : l.name;
                  const statusCls =
                    status === "saved"
                      ? "border-primary bg-primary/15 text-foreground ring-1 ring-primary/40"
                      : status === "unique"
                        ? "border-amber-500/60 bg-amber-500/10 text-foreground"
                        : status === "error"
                          ? "border-destructive bg-destructive/10 text-foreground"
                          : "";
                  return (
                    <button
                      key={l.id}
                      type="button"
                      disabled={!isEligible || busy}
                      onClick={() => isEligible && setSelected((s) => ({ ...s, [l.id]: !s[l.id] }))}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition ${
                        !isEligible
                          ? "border-border bg-muted/40 text-muted-foreground line-through"
                          : statusCls
                            ? statusCls
                            : checked
                              ? "border-primary bg-primary/10 text-foreground"
                              : "border-border bg-background text-muted-foreground"
                      }`}
                      title={reasonLabel}
                      aria-label={`${l.name} — ${reasonLabel}`}
                    >
                      {status === "saved" && <Check className="h-3 w-3 text-primary" />}
                      {status === "unique" && <AlertTriangle className="h-3 w-3 text-amber-500" />}
                      {status === "error" && <XIcon className="h-3 w-3 text-destructive" />}
                      {!status && checked && <Check className="h-3 w-3" />}
                      {!isEligible && <Lock className="h-3 w-3" />}
                      <span className="font-medium truncate max-w-[140px]">{l.name}</span>
                      {existing && (
                        <span className="tabular-nums opacity-70">
                          {existing.predicted_home_score}:{existing.predicted_away_score}
                        </span>
                      )}
                    </button>
                  );
                })}
            </div>
            {Object.keys(lastStatus).length > 0 && (
              <p className="text-[11px] text-muted-foreground" aria-live="polite">
                {(() => {
                  const counts = Object.values(lastStatus).reduce(
                    (acc, s) => ((acc[s] = (acc[s] ?? 0) + 1), acc),
                    {} as Record<string, number>,
                  );
                  const parts: string[] = [];
                  if (counts.saved) parts.push(`✓ ${counts.saved} ${t("bulk.status.saved").toLowerCase()}`);
                  if (counts.unique) parts.push(`⚠ ${counts.unique} ${t("bulk.status.skippedUnique").toLowerCase()}`);
                  if (counts.error) parts.push(`✕ ${counts.error} ${t("bulk.status.error").toLowerCase()}`);
                  return parts.join(" · ");
                })()}
              </p>
            )}

            <div className="flex justify-end">
              <Button onClick={save} disabled={busy || targetLeagueIds.length === 0} size="sm" className="rounded-full bg-gradient-hero text-primary-foreground border-0">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <><Save className="h-4 w-4 mr-2" />{t("bulk.saveToN", { count: targetLeagueIds.length })}</>
                )}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

/* -------------------- copy section -------------------- */

const CopySection = ({
  leagues, matches, predictions, userId, onDone,
}: {
  leagues: League[];
  matches: MatchData[];
  predictions: Prediction[];
  userId: string;
  onDone: (rows: Prediction[]) => void;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState<string>("");
  const [targets, setTargets] = useState<Record<string, boolean>>({});
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState<"idle" | "prepare" | "validate" | "save" | "done">("idle");
  const [stepMsg, setStepMsg] = useState<string>("");
  const [result, setResult] = useState<{ copied: number; skippedLocked: number; skippedExisting: number } | null>(null);
  type SkipReason = "locked" | "existing" | "unique" | "error";
  type SkipDetail = { matchId: string; leagueId: string; reason: SkipReason; message?: string };
  const [skipDetails, setSkipDetails] = useState<SkipDetail[]>([]);
  const [skipsOpen, setSkipsOpen] = useState(false);

  const matchById = useMemo(() => {
    const m: Record<string, MatchData> = {};
    matches.forEach((x) => { m[x.id] = x; });
    return m;
  }, [matches]);

  const sourceLeague = leagues.find((l) => l.id === source);
  const possibleTargets = useMemo(
    () => leagues.filter((l) => l.id !== source && l.tournament_id === sourceLeague?.tournament_id),
    [leagues, source, sourceLeague]
  );

  const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

  const handleCopy = async () => {
    if (!source) { toast.error(t("bulk.pickSource")); return; }
    const targetIds = possibleTargets.filter((l) => targets[l.id]).map((l) => l.id);
    if (targetIds.length === 0) { toast.error(t("bulk.pickTarget")); return; }

    const sourcePreds = predictions.filter((p) => p.league_id === source);
    setBusy(true);
    setResult(null);
    setSkipDetails([]);
    const details: SkipDetail[] = [];

    // Step 1: prepare
    setStep("prepare");
    setStepMsg(t("bulk.copySteps.prepare", { sourceName: sourceLeague?.name ?? "" }));
    await wait(250);

    // Step 2: validate locks / build rows
    setStep("validate");
    setStepMsg(t("bulk.copySteps.validate", { total: sourcePreds.length, targets: targetIds.length }));
    await wait(250);

    const { rows, skippedLocked, skippedExisting } = buildBulkCopyRows({
      sourceLeagueId: source,
      targetLeagueIds: targetIds,
      leagues,
      matches,
      predictions,
      userId,
      overwrite,
    });
    const copied = rows.length;

    // Mirror buildBulkCopyRows logic to capture per-row skip reasons (locked/existing)
    const nowMs = Date.now();
    for (const sp of sourcePreds) {
      const m = matchById[sp.match_id];
      if (!m) continue;
      for (const tid of targetIds) {
        const tLeague = leagues.find((l) => l.id === tid);
        if (!tLeague) continue;
        const lockedAt = new Date(m.kickoff_at).getTime() - tLeague.lock_minutes_before_kickoff * 60_000;
        if (lockedAt <= nowMs) {
          details.push({ matchId: sp.match_id, leagueId: tid, reason: "locked" });
          continue;
        }
        const exists = predictions.some(
          (p) => p.league_id === tid && p.match_id === sp.match_id,
        );
        if (exists && !overwrite) {
          details.push({ matchId: sp.match_id, leagueId: tid, reason: "existing" });
        }
      }
    }

    // Step 3: save (per-row so partial successes survive validation failures)
    setStep("save");
    setStepMsg(t("bulk.copySteps.save", { count: copied }));

    let savedRows = 0;
    const successfulRows: { league_id: string; match_id: string; user_id: string; predicted_home_score: number; predicted_away_score: number }[] = [];
    const uniqueSkippedByLeague: Record<string, number> = {};
    const otherErrors: string[] = [];
    for (const row of rows) {
      const { error } = await supabase
        .from("predictions")
        .upsert(row, { onConflict: "league_id,match_id,user_id" });
      if (!error) {
        savedRows++;
        successfulRows.push(row);
      } else if (
        error.hint === "unique_results_taken" ||
        error.message?.includes("already taken by another player")
      ) {
        uniqueSkippedByLeague[row.league_id] =
          (uniqueSkippedByLeague[row.league_id] ?? 0) + 1;
        details.push({ matchId: row.match_id, leagueId: row.league_id, reason: "unique" });
      } else {
        otherErrors.push(error.message);
        details.push({ matchId: row.match_id, leagueId: row.league_id, reason: "error", message: error.message });
      }
    }
    setSkipDetails(details);
    setSkipsOpen(details.length > 0);

    const uniqueSkippedTotal = Object.values(uniqueSkippedByLeague).reduce((a, b) => a + b, 0);
    const uniqueSkippedLeagueNames = Object.keys(uniqueSkippedByLeague)
      .map((lid) => leagues.find((l) => l.id === lid)?.name ?? lid);

    setStep("done");
    setStepMsg(savedRows === 0 ? t("bulk.copySteps.nothing") : t("bulk.copySteps.done"));
    setResult({ copied: savedRows, skippedLocked, skippedExisting: skippedExisting + uniqueSkippedTotal });
    setBusy(false);
    const copyDescParts: string[] = [];
    if (uniqueSkippedTotal > 0) {
      copyDescParts.push(
        t("bulk.uniqueSkippedCopy", {
          count: uniqueSkippedTotal,
          leagues: uniqueSkippedLeagueNames.join(", "),
        })
      );
    }
    const copyDedupedErrors = [...new Set(otherErrors)];
    if (copyDedupedErrors.length) {
      copyDescParts.push(...copyDedupedErrors);
    }
    if (savedRows > 0) {
      toast.success(t("bulk.copyResult", { copied: savedRows, skippedLocked, skippedExisting }), {
        description: copyDescParts.length ? copyDescParts.join("\n") : undefined,
      });
    } else {
      toast(t("bulk.copyResult", { copied: savedRows, skippedLocked, skippedExisting }), {
        description: copyDescParts.length ? copyDescParts.join("\n") : undefined,
      });
    }
    onDone(
      successfulRows.map((r) => ({
        id: crypto.randomUUID(),
        league_id: r.league_id,
        match_id: r.match_id,
        user_id: r.user_id,
        predicted_home_score: r.predicted_home_score,
        predicted_away_score: r.predicted_away_score,
      }))
    );
  };


  const stepOrder: Array<"prepare" | "validate" | "save" | "done"> = ["prepare", "validate", "save", "done"];
  const stepIndex = step === "idle" ? -1 : stepOrder.indexOf(step);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer">
            <CardTitle className="flex items-center justify-between gap-2 text-base">
              <span className="flex items-center gap-2">
                <CopyIcon className="h-4 w-4" />
                {t("bulk.copyTitle")}
              </span>
              <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </CardTitle>
            <CardDescription>{t("bulk.copyDesc")}</CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">{t("bulk.fromLeague")}</Label>
            <Select value={source} onValueChange={(v) => { setSource(v); setTargets({}); setResult(null); setStep("idle"); }}>
              <SelectTrigger><SelectValue placeholder={t("bulk.pickLeague")} /></SelectTrigger>
              <SelectContent>
                {leagues.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{t("bulk.toLeagues")}</Label>
            {source ? (
              possibleTargets.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-2">{t("bulk.noTargets")}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {possibleTargets.map((l) => {
                    const checked = !!targets[l.id];
                    return (
                      <button key={l.id} type="button"
                        onClick={() => setTargets((s) => ({ ...s, [l.id]: !s[l.id] }))}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition ${
                          checked ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"
                        }`}>
                        {checked && <Check className="h-3 w-3" />}
                        <span className="font-medium">{l.name}</span>
                      </button>
                    );
                  })}
                </div>
              )
            ) : (
              <p className="text-xs text-muted-foreground mt-2">{t("bulk.pickSourceFirst")}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Checkbox id="overwrite" checked={overwrite} onCheckedChange={(v) => setOverwrite(!!v)} />
          <Label htmlFor="overwrite" className="text-xs cursor-pointer">{t("bulk.overwrite")}</Label>
        </div>

        <Button onClick={handleCopy} disabled={busy || !source} className="rounded-full">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><CopyIcon className="h-4 w-4 mr-2" />{t("bulk.copyBtn")}</>)}
        </Button>

        {/* Step progress */}
        {step !== "idle" && (
          <div
            role="status"
            aria-live="polite"
            className="rounded-lg border border-border bg-muted/40 p-3 space-y-2 animate-fade-in"
          >
            <div className="flex items-center gap-2 text-sm">
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              ) : (
                <Check className="h-4 w-4 text-primary" />
              )}
              <span>{stepMsg}</span>
            </div>
            {/* progress bar */}
            <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${((stepIndex + 1) / stepOrder.length) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Result counters */}
        {result && (
          <div className="grid grid-cols-3 gap-2 animate-fade-in">
            <StatTile label={t("bulk.copyStats.saved")} value={result.copied} tone="primary" />
            <StatTile label={t("bulk.copyStats.skippedLocked")} value={result.skippedLocked} tone="warning" />
            <StatTile label={t("bulk.copyStats.skippedExisting")} value={result.skippedExisting} tone="muted" />
          </div>
        )}

        {/* Skipped details */}
        {result && skipDetails.length > 0 && (
          <Collapsible open={skipsOpen} onOpenChange={setSkipsOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex w-full items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm hover:bg-muted/50 transition"
              >
                <span className="inline-flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  {t("bulk.skippedDetails", { count: skipDetails.length })}
                </span>
                <ChevronDown className={`h-4 w-4 transition-transform ${skipsOpen ? "rotate-180" : ""}`} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <ul className="mt-2 divide-y divide-border rounded-lg border border-border bg-background overflow-hidden">
                {skipDetails.map((d, i) => {
                  const m = matchById[d.matchId];
                  const lg = leagues.find((l) => l.id === d.leagueId);
                  const reasonCls =
                    d.reason === "locked"
                      ? "bg-destructive/10 text-destructive border-destructive/30"
                      : d.reason === "unique"
                        ? "bg-amber-500/10 text-amber-600 border-amber-500/40"
                        : d.reason === "error"
                          ? "bg-destructive/10 text-destructive border-destructive/30"
                          : "bg-muted text-muted-foreground border-border";
                  const reasonLabel =
                    d.reason === "locked"
                      ? t("bulk.status.skippedLocked")
                      : d.reason === "existing"
                        ? t("bulk.status.skippedExisting")
                        : d.reason === "unique"
                          ? t("bulk.status.skippedUnique")
                          : t("bulk.status.error");
                  return (
                    <li key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">
                          {m ? `${m.home_team} vs ${m.away_team}` : d.matchId}
                        </div>
                        <div className="text-muted-foreground truncate">{lg?.name ?? d.leagueId}</div>
                      </div>
                      <span
                        className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${reasonCls}`}
                        title={d.message}
                      >
                        {reasonLabel}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

const StatTile = ({ label, value, tone }: { label: string; value: number; tone: "primary" | "warning" | "muted" }) => {
  const toneCls =
    tone === "primary"
      ? "border-primary/40 bg-primary/10 text-foreground"
      : tone === "warning"
      ? "border-destructive/40 bg-destructive/10 text-foreground"
      : "border-border bg-muted/40 text-muted-foreground";
  return (
    <div className={`rounded-lg border px-3 py-2 ${toneCls}`}>
      <div className="text-xs">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
};

export default BulkTips;
